import { styled } from '@mui/material';
import type Big from 'big.js';
import dayjs from 'dayjs';
import isUndefined from 'lodash/isUndefined';

import {
  ChartTooltipElementProps,
  DataPopperRow,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { WorkloadTrafficDestinationHistoryItem } from '@cast/types';
import { getCurrencyPrecision } from '@cast/utils';

import { Money } from 'components/money';
import { NetworkResourceType } from 'features/cluster/cost-report/network-cost/_types';
import { trafficTickFormatter } from 'features/cluster/cost-report/network-cost/_utils';

import { TOP_DESTINATIONS_COLOR_MAP } from '../_constants';
import { Datapoint } from '../_types';

type TooltipRowProps = {
  name: string;
  value: number | undefined | null;
  color: string;
  resourceType: NetworkResourceType;
};

const FormattedMoney = styled(Money)(({ theme }) => ({
  color: theme.palette.grey[900],
  ...theme.typography.P10R,
}));

const FormattedTraffic = ({ value }: { value: number }) => {
  return (
    <Typography variant="P10R" color="grey.900">
      {trafficTickFormatter(value)}
    </Typography>
  );
};

const TooltipRow = ({ name, value, color, resourceType }: TooltipRowProps) => {
  const precision = value !== null ? getCurrencyPrecision(value) : 0;

  return (
    <DataPopperRow
      indicator={{ size: 'small', color, variant: 'outlined' }}
      left={
        <Typography variant="P10R" noWrap>
          {name}
        </Typography>
      }
      right={
        <Stack direction="row" gap="10px">
          {value !== null && !isUndefined(value) ? (
            resourceType === NetworkResourceType.COSTS ? (
              <FormattedMoney
                amount={value}
                precision={precision}
                fixed={precision}
              />
            ) : (
              <FormattedTraffic value={value} />
            )
          ) : (
            '--'
          )}
        </Stack>
      }
    />
  );
};

type Props = {
  topItems: WorkloadTrafficDestinationHistoryItem<Big>[];
  timezone: string;
  resourceType: NetworkResourceType;
};

type TooltipData = Datapoint & { timestamp: string };

export const makeTopDestinationsTooltip = ({
  topItems,
  timezone,
  resourceType,
}: Props) => {
  const tooltipHeader = ({
    data,
    testId,
  }: ChartTooltipElementProps<TooltipData>) => (
    <DataPopperRow
      testId={testId}
      dividerBottom
      left={
        <Typography variant="L10M" color={tokens.theme.fg.structural.secondary}>
          {dayjs.utc(data.timestamp).tz(timezone).format('MMM D')}
        </Typography>
      }
    />
  );

  const tooltipFooter = ({ data }: ChartTooltipElementProps<TooltipData>) => {
    const values = [
      data.workload1,
      data.workload2,
      data.workload3,
      data.workload4,
      data.workload5,
    ];

    return (
      <>
        {topItems.map((item, index) => (
          <TooltipRow
            key={
              (item.destination?.workloadName || '') +
              (item.destination?.workloadType || '') +
              (item.destination?.namespace || '')
            }
            name={item.destination?.workloadName || ''}
            value={values[index]}
            color={TOP_DESTINATIONS_COLOR_MAP[index]}
            resourceType={resourceType}
          />
        ))}
      </>
    );
  };

  return { tooltipHeader, tooltipFooter };
};
